import type { Attachment } from '@medplum/fhirtypes'
import { getBinaryId, saveBlobWithName } from '@seen/core/utils'
import { Button } from '@seen/ui'
import { IconDownload, IconExternalLink, IconLink } from '@tabler/icons-react'
import { type JSX, type ReactNode, useCallback } from 'react'
import { isExternalUrl } from '#src/components/attachments/attachment-utils.ts'
import { ExternalLink } from '#src/components/ExternalLink.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export interface AttachmentCardProps {
  attachment: Attachment
  onTitleChange?: (title: string) => void
  action?: 'open' | 'download' // We default to open because it's typically a better UX to quickly be able to see the attachment in a new tab instead of downloading it.
  children?: ReactNode
}

export function AttachmentCard({
  attachment,
  onTitleChange,
  action = 'open',
  children,
}: AttachmentCardProps): JSX.Element {
  const debouncedOnTitleChange = useDebouncedCallback(
    onTitleChange ? onTitleChange : () => {},
    1000
  )

  const isUrlExternal = isExternalUrl(attachment.url)

  return (
    <div className="group flex h-16 w-full items-center gap-4 rounded-sm border border-muted-border bg-muted px-4">
      {isUrlExternal ? (
        <IconLink className="size-4 text-foreground-tertiary" />
      ) : (
        <FileIcon className="text-foreground-tertiary" />
      )}
      <div className="flex flex-1 flex-col">
        {onTitleChange ? (
          <RichTextEditor
            variant="unstyled"
            className="break-all"
            value={attachment.title ?? ''}
            onChange={(value) => debouncedOnTitleChange(value)}
          />
        ) : (
          <p className="">{attachment.title}</p>
        )}
        {attachment?.size && !isUrlExternal && (
          <span className="text-3xs text-foreground-secondary">
            {(attachment.size / 1000).toFixed(2)} KB
          </span>
        )}
      </div>
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex shrink-0 items-center gap-2"
      >
        {action === 'open' && !isUrlExternal && (
          <ExternalLink
            className="rounded-md p-3 text-foreground-secondary opacity-0 group-hover:opacity-100 hover:bg-muted-hover hover:text-foreground"
            href={attachment?.url}
          />
        )}
        {action === 'download' && !isUrlExternal && (
          <DownloadButton attachment={attachment} />
        )}
        {isUrlExternal && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => window.open(attachment.url, '_blank')}
            className="hover:bg-muted-hover focus-visible:bg-muted-hover"
            title="Open in new tab"
          >
            <IconExternalLink className="size-4" />
          </Button>
        )}
        {children}
      </div>
    </div>
  )
}

function DownloadButton({
  attachment,
}: {
  attachment: Attachment
}): JSX.Element {
  const medplum = useMedplum()
  const downloadAttachment = useCallback(async (): Promise<void> => {
    if (!attachment.url) {
      return
    }
    const blob = await medplum.downloadAttachmentAsBinary(attachment.url)
    saveBlobWithName(blob, getBinaryId(attachment.url) ?? attachment.title)
  }, [attachment.url, attachment.title, medplum])
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={downloadAttachment}
      className="hover:bg-muted-hover focus-visible:bg-muted-hover aria-expanded:bg-muted-hover data-[active]:bg-muted-hover data-[state=open]:bg-muted-hover"
    >
      <IconDownload className="size-4" />
    </Button>
  )
}

function FileIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      role="img"
      focusable="false"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.75 1V6.75C6.75 7.5297 7.34489 8.17045 8.10554 8.24313L8.25 8.25H14V13C14 14.1046 13.1046 15 12 15H4C2.89543 15 2 14.1046 2 13V3C2 1.89543 2.89543 1 4 1H6.75ZM8 1L14 7.03022H9C8.44772 7.03022 8 6.5825 8 6.03022V1Z"
      ></path>
    </svg>
  )
}
